import { Alert } from '@creditornot/cb-alert';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { usePluralization } from 'common/hooks';

import { MultiImageFileError } from './MultiImageFileSelector';
import { MIN_WIDTH, MIN_HEIGHT } from '../constants';

type Props = {
  errors: MultiImageFileError[];
  countLimit: number;
};

const ErrorGroup = styled.ul`
  margin: 0;
  padding: 0;
  list-style-position: inside;
`;

const ErrorListItem = styled.li`
  overflow-wrap: break-word;
`;

const UploadErrorsAlert = ({ errors, countLimit }: Props) => {
  const intl = useIntl();
  const { formatPluralMessage } = usePluralization();

  const overCountLimitErrors = errors.filter(error => error.type === 'error-over-limit-count');
  const sizeErrors = errors.filter(error => error.type === 'error-image-too-small');
  const unknownErrors = errors.filter(error => error.type === 'error-unknown');

  return (
    <div>
      {(sizeErrors.length > 0 || unknownErrors.length > 0) && (
        <Alert
          style={{ marginBlockStart: '12px' }}
          title={formatPluralMessage({
            id: 'image-upload.multiImage.errors.failedToUploadImages',
            count: errors.length,
          })}
          variant="error"
          size="small"
          showMoreContent={
            <div>
              {sizeErrors.length > 0 && (
                <ErrorGroup style={{ marginBlockEnd: '4px' }}>
                  <FormattedMessage
                    id="image-upload.errorTooSmall"
                    values={{
                      width: MIN_WIDTH,
                      height: MIN_HEIGHT,
                    }}
                  />{' '}
                  {sizeErrors.map(error => (
                    <ErrorListItem>{error.fileName}</ErrorListItem>
                  ))}
                </ErrorGroup>
              )}
              {unknownErrors.length > 0 && (
                <ErrorGroup>
                  <FormattedMessage id="errors.unknown-error-occurred" />
                  {unknownErrors.map(error => (
                    <ErrorListItem>{error.fileName}</ErrorListItem>
                  ))}
                </ErrorGroup>
              )}
            </div>
          }
        />
      )}
      {overCountLimitErrors.length > 0 && (
        <Alert
          style={{ marginBlockStart: '12px' }}
          title={intl.formatMessage(
            {
              id: 'image-upload.multiImage.errors.overLimitCount.header',
            },
            { count: countLimit },
          )}
          variant="error"
          size="small"
          showMoreContent={<FormattedMessage id="image-upload.multiImage.errors.overLimitCount.description" />}
        />
      )}
    </div>
  );
};

export default UploadErrorsAlert;
